import {
  createContext,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { addMonths, currentMonth, periodOfMonth, yearToDate, type Period } from '@alexiarest/shared';
import { demoPeriod } from '../api/demo';
import { isDemoMode } from '../api/client';

export interface PeriodPreset {
  id: string;
  label: string;
  period: Period;
}

function lastMonths(count: number): Period {
  const end = currentMonth();
  const start = addMonths(end, -(count - 1));
  return { start: periodOfMonth(start).start, end: periodOfMonth(end).end };
}

export function buildPresets(): PeriodPreset[] {
  const presets: PeriodPreset[] = [
    { id: 'mes', label: 'Mes en curso', period: periodOfMonth(currentMonth()) },
    { id: '3m', label: 'Últimos 3 meses', period: lastMonths(3) },
    { id: '6m', label: 'Últimos 6 meses', period: lastMonths(6) },
    { id: '12m', label: 'Últimos 12 meses', period: lastMonths(12) },
    { id: 'ytd', label: 'Año en curso', period: yearToDate() },
  ];
  if (isDemoMode) {
    presets.push({ id: 'demo', label: 'Todos los datos de ejemplo', period: demoPeriod });
  }
  return presets;
}

interface PeriodState {
  period: Period;
  setPeriod: (period: Period) => void;
  presets: PeriodPreset[];
}

const PeriodContext = createContext<PeriodState | null>(null);

export function PeriodProvider({ children }: { children: ReactNode }): JSX.Element {
  const presets = useMemo(buildPresets, []);
  /*
   * Se arranca en «Mes en curso», en la demo y en las cuentas reales (Alex,
   * 4 sep 2026). Antes eran los ultimos seis meses, y en una cuenta con pocos
   * meses cargados eso mete meses vacios en las comparativas y los
   * indicadores salen raros la primera vez que se entra. El mes en curso es
   * el periodo donde seguro hay algo, y desde ahi el usuario amplia.
   *
   * La demo lleva el mes en curso completo precisamente para que este
   * arranque no la deje en blanco (ver seed.ts).
   */
  const [period, setPeriod] = useState<Period>(() => periodOfMonth(currentMonth()));

  const value = useMemo<PeriodState>(
    () => ({ period, setPeriod, presets }),
    [period, presets],
  );

  return <PeriodContext.Provider value={value}>{children}</PeriodContext.Provider>;
}

export function usePeriod(): PeriodState {
  const ctx = useContext(PeriodContext);
  if (!ctx) throw new Error('usePeriod debe usarse dentro de <PeriodProvider>.');
  return ctx;
}
